/**
 * Admin: Verifier Organization Management — /admin/verifiers
 * Manage accredited verifier orgs + users + assign assessments.
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Shield, Plus, RefreshCw, Users, Check, X, DollarSign, ChevronDown, ChevronRight } from "lucide-react";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const COMMERCIAL_LABELS = {
  quote_pending:"Quote Pending", quote_entered:"Quote Entered",
  awaiting_client_approval:"Awaiting Client Approval",
  approved_to_proceed:"Approved — Ready", on_hold:"On Hold",
  in_verification:"In Verification", completed:"Completed",
};
const COMMERCIAL_COLORS = {
  quote_pending:"bg-slate-100 text-slate-500", quote_entered:"bg-blue-50 text-blue-700",
  awaiting_client_approval:"bg-amber-50 text-amber-700",
  approved_to_proceed:"bg-emerald-50 text-emerald-700", on_hold:"bg-red-50 text-red-700",
  in_verification:"bg-indigo-50 text-indigo-700", completed:"bg-emerald-50 text-emerald-700",
};
const PAYMENT_COLORS = {
  not_invoiced:"bg-slate-100 text-slate-500", invoiced:"bg-blue-50 text-blue-700",
  partially_paid:"bg-amber-50 text-amber-700", paid:"bg-emerald-50 text-emerald-700",
};

export default function AdminVerifiers() {
  const [orgs,      setOrgs]    = useState([]);
  const [engagements,setEngs]  = useState([]);
  const [loading,   setLoading] = useState(true);
  const [showAdd,   setShowAdd] = useState(false);
  const [showUser,  setShowUser]= useState(null);
  const [showAssign,setShowAssign]=useState(false);
  const [showEngModal,setEngModal]=useState(null); // assignment_id for engagement creation
  const [showEngEdit, setEngEdit]=useState(null);  // engagement_id for editing
  const [assessments,setAssess] = useState([]);
  const [activeTab, setActiveTab] = useState("registry"); // registry | engagements

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, aRes, eRes] = await Promise.allSettled([
        client.get("/admin/verifiers/"),
        client.get("/assessments"),
        client.get("/admin/verifier-engagements/"),
      ]);
      if (oRes.status === "fulfilled") setOrgs(oRes.value.data || []);
      if (aRes.status === "fulfilled") setAssess(aRes.value.data || []);
      if (eRes.status === "fulfilled") setEngs(eRes.value.data?.engagements || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-8 min-h-screen bg-[#F8FAFC]" data-testid="admin-verifiers">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield size={20} className="text-indigo-600" />
            <PageHeader title="Verifier Registry" />
          </div>
          <p className="text-sm text-slate-500">Manage accredited third-party verifier organizations and commercial engagements.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEngModal("new")} data-testid="create-engagement-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700">
            <DollarSign size={14} /> New Engagement
          </button>
          <button onClick={() => setShowAssign(true)} data-testid="assign-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700">
            <Plus size={14} /> Assign Verification
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50">
            <Plus size={14} /> Add Verifier Org
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-5 w-fit">
        {[["registry","Verifier Registry"],["engagements","Commercial Engagements"]].map(([tab,label])=>(
          <button key={tab} onClick={()=>setActiveTab(tab)}
            className={`px-4 py-2 text-xs font-medium rounded-lg transition-all ${activeTab===tab?"bg-[#0F172A] text-white":"text-slate-500 hover:text-slate-700"}`}>
            {label} {tab==="engagements" && engagements.length>0 && <span className="ml-1 text-xs bg-amber-500 text-white rounded-full px-1.5">{engagements.filter(e=>e.commercial_status==="awaiting_client_approval").length||""}</span>}
          </button>
        ))}
      </div>

      {/* Registry tab */}
      {activeTab === "registry" && (
        <div className="space-y-4">
        {loading ? [...Array(2)].map((_,i)=>(
          <div key={i} className="h-24 bg-white border border-slate-200 rounded-xl animate-pulse" />
        )) : orgs.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Shield size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No verifier organizations yet.</p>
          </div>
        ) : orgs.map(org => (
          <div key={org.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`verifier-org-${org.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-slate-900">{org.name}</p>
                  <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 rounded text-slate-500">{org.org_id}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${org.status==="active"?"bg-emerald-50 text-emerald-700":"bg-red-50 text-red-700"}`}>{org.status}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(org.accreditations||[]).map(a=>(
                    <span key={a} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full">{a}</span>
                  ))}
                  {(org.regions||[]).map(r=>(
                    <span key={r} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{r}</span>
                  ))}
                </div>
              </div>
              <button onClick={() => setShowUser(org.id)}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 border border-indigo-200 rounded-lg px-2.5 py-1.5">
                <Users size={12} /> Add User
              </button>
            </div>
            {/* Users */}
            {(org.users||[]).length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-400 mb-2">Users ({org.users.length})</p>
                <div className="flex flex-wrap gap-2">
                  {org.users.map(u=>(
                    <div key={u.id} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-50 rounded-lg border border-slate-200 text-xs">
                      <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-xs">
                        {u.first_name?.[0]}{u.last_name?.[0]}
                      </div>
                      <span className="font-medium text-slate-700">{u.first_name} {u.last_name}</span>
                      <span className="text-slate-400">({u.role?.replace(/_/g," ")})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        </div>
      )} {/* end registry tab */}

      {/* Engagements tab */}
      {activeTab === "engagements" && (
        <div className="space-y-3">
          {engagements.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <DollarSign size={32} className="mx-auto mb-2 opacity-30"/>
              <p className="text-sm">No engagements yet. Create one after assigning a verification.</p>
            </div>
          ) : engagements.map(e => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`engagement-${e.engagement_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-slate-900">{e.assessment_title}</p>
                    <span className="text-xs font-mono px-2 py-0.5 bg-slate-100 rounded text-slate-500">{e.engagement_id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${COMMERCIAL_COLORS[e.commercial_status]||"bg-slate-100 text-slate-500"}`}>
                      {COMMERCIAL_LABELS[e.commercial_status]||e.commercial_status}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PAYMENT_COLORS[e.payment_status]||"bg-slate-100 text-slate-500"}`}>
                      {e.payment_status?.replace(/_/g," ")}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">Verifier: {e.verifier_org_name}</p>
                  {e.fee_notes && <p className="text-xs text-slate-400 mt-0.5 italic">"{e.fee_notes}"</p>}
                </div>
                <div className="flex items-center gap-3">
                  {e.quoted_fee != null && (
                    <div className="text-right">
                      <p className="text-base font-bold text-slate-900">{e.quoted_fee.toLocaleString()} {e.currency}</p>
                      <p className="text-xs text-slate-400">Quoted</p>
                    </div>
                  )}
                  <button onClick={() => setEngEdit(e.engagement_id)}
                    className="flex items-center gap-1 text-xs text-indigo-600 border border-indigo-200 rounded-lg px-2.5 py-1.5 hover:bg-indigo-50">
                    Edit
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showAdd && <AddOrgModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {showUser && <AddUserModal orgId={showUser} onClose={() => setShowUser(null)} onSaved={() => { setShowUser(null); load(); }} />}
      {showAssign && <AssignModal orgs={orgs} assessments={assessments} onClose={() => setShowAssign(false)} onSaved={() => { setShowAssign(false); toast.success("Assessment assigned to verifier."); load(); }} />}
      {(showEngModal || showEngEdit) && (
        <EngagementModal
          assignmentId={showEngModal !== "new" ? showEngModal : null}
          engagementId={showEngEdit}
          onClose={() => { setEngModal(null); setEngEdit(null); }}
          onSaved={() => { setEngModal(null); setEngEdit(null); load(); }}
        />
      )}
    </div>
  );
}

function AddOrgModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name:"", accreditations:"ISO 14064, ISO 14065", regions:"Africa", contact_email:"" });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await client.post("/admin/verifiers/", {
        name: form.name, contact_email: form.contact_email,
        accreditations: form.accreditations.split(",").map(s=>s.trim()).filter(Boolean),
        regions: form.regions.split(",").map(s=>s.trim()).filter(Boolean),
      });
      toast.success("Verifier organization created");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold text-slate-900">Add Verifier Organization</h3>
          <button onClick={onClose}><X size={16} className="text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          {[["name","Organization Name *","e.g. SGS Nigeria"],["contact_email","Contact Email","verify@sgs.ng"],["accreditations","Accreditations (comma-separated)","ISO 14064, ISO 14065"],["regions","Regions (comma-separated)","Africa, Global"]].map(([k,l,p])=>(
            <div key={k}><label className="text-xs font-medium text-slate-600 mb-1 block">{l}</label>
              <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={p}
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSave} disabled={!form.name.trim()||saving}
            className="flex-1 h-10 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Creating..." : "Create Organization"}
          </button>
          <button onClick={onClose} className="px-4 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AddUserModal({ orgId, onClose, onSaved }) {
  const [form, setForm] = useState({ first_name:"", last_name:"", email:"", password:"", role:"lead_verifier" });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await client.post(`/admin/verifiers/${orgId}/users`, form);
      toast.success("Verifier user added");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold text-slate-900">Add Verifier User</h3>
          <button onClick={onClose}><X size={16} className="text-slate-400"/></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            {[["first_name","First Name"],["last_name","Last Name"]].map(([k,l])=>(
              <div key={k}><label className="text-xs font-medium text-slate-600 mb-1 block">{l}</label>
                <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                  className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" /></div>
            ))}
          </div>
          {[["email","Email"],["password","Password (min 8 chars)"]].map(([k,l])=>(
            <div key={k}><label className="text-xs font-medium text-slate-600 mb-1 block">{l}</label>
              <input type={k==="password"?"password":"email"} value={form[k]} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))}
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" /></div>
          ))}
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Role</label>
            <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500">
              <option value="lead_verifier">Lead Verifier (can decide)</option>
              <option value="reviewer">Reviewer (view only)</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSave} disabled={!form.email||!form.password||saving}
            className="flex-1 h-10 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Adding..." : "Add User"}
          </button>
          <button onClick={onClose} className="px-4 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ orgs, assessments, onClose, onSaved }) {
  const [form, setForm] = useState({ assessment_id:"", verifier_org_id:"", due_date:"", verification_scope:"Full GHG inventory verification per ISO 14064-1" });
  const [saving, setSaving] = useState(false);
  const calcAssessments = assessments.filter(a=>["approved","issued","analysis","review"].includes(a.status));
  const handleSave = async () => {
    setSaving(true);
    try {
      await client.post("/admin/verifiers/assign", {
        assessment_id: Number(form.assessment_id),
        verifier_org_id: Number(form.verifier_org_id),
        due_date: form.due_date || undefined,
        verification_scope: form.verification_scope,
      });
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to assign"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold text-slate-900">Assign for Third-Party Verification</h3>
          <button onClick={onClose}><X size={16} className="text-slate-400"/></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Assessment</label>
            <select value={form.assessment_id} onChange={e=>setForm(f=>({...f,assessment_id:e.target.value}))}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
              <option value="">Select assessment...</option>
              {calcAssessments.map(a=><option key={a.id} value={a.id}>{a.title} {a.reporting_year?`(FY${a.reporting_year})`:""}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Verifier Organization</label>
            <select value={form.verifier_org_id} onChange={e=>setForm(f=>({...f,verifier_org_id:e.target.value}))}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
              <option value="">Select verifier...</option>
              {orgs.filter(o=>o.status==="active").map(o=><option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Due Date (optional)</label>
            <input type="date" value={form.due_date} onChange={e=>setForm(f=>({...f,due_date:e.target.value}))}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Verification Scope</label>
            <textarea value={form.verification_scope} onChange={e=>setForm(f=>({...f,verification_scope:e.target.value}))} rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSave} disabled={!form.assessment_id||!form.verifier_org_id||saving}
            className="flex-1 h-10 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50">
            {saving ? "Assigning..." : "Assign Verification"}
          </button>
          <button onClick={onClose} className="px-4 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}


function EngagementModal({ assignmentId, engagementId, onClose, onSaved }) {
  const [form, setForm] = useState({
    assignment_id:"", quoted_fee:"", currency:"USD",
    fee_notes:"", commercial_status:"quote_entered", payment_status:"not_invoiced"
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!engagementId;

  useEffect(() => {
    if (isEdit) {
      client.get(`/admin/verifier-engagements/${engagementId}`)
        .then(r => setForm({
          quoted_fee: r.data.quoted_fee || "",
          currency: r.data.currency || "USD",
          fee_notes: r.data.fee_notes || "",
          commercial_status: r.data.commercial_status || "quote_entered",
          payment_status: r.data.payment_status || "not_invoiced",
        })).catch(() => {});
    }
    if (assignmentId) setForm(f => ({...f, assignment_id: assignmentId}));
  }, [isEdit, engagementId, assignmentId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await client.patch(`/admin/verifier-engagements/${engagementId}`, {
          quoted_fee: form.quoted_fee ? Number(form.quoted_fee) : null,
          currency: form.currency, fee_notes: form.fee_notes,
          commercial_status: form.commercial_status,
        });
        if (form.payment_status) {
          await client.patch(`/admin/verifier-engagements/${engagementId}/payment-status`, { payment_status: form.payment_status });
        }
      } else {
        await client.post("/admin/verifier-engagements/", {
          assignment_id: Number(form.assignment_id),
          quoted_fee: form.quoted_fee ? Number(form.quoted_fee) : null,
          currency: form.currency, fee_notes: form.fee_notes,
          commercial_status: form.commercial_status,
        });
      }
      toast.success(isEdit ? "Engagement updated" : "Engagement created");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const COMMERCIAL_STATUSES = ["quote_pending","quote_entered","awaiting_client_approval","approved_to_proceed","on_hold","in_verification","completed"];
  const PAYMENT_STATUSES    = ["not_invoiced","invoiced","partially_paid","paid"];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-bold text-slate-900">{isEdit ? "Edit Engagement" : "Create Engagement"}</h3>
          <button onClick={onClose}><X size={16} className="text-slate-400"/></button>
        </div>
        <div className="space-y-3">
          {!isEdit && (
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Assignment ID</label>
              <input type="number" value={form.assignment_id} onChange={e=>setForm(f=>({...f,assignment_id:e.target.value}))}
                placeholder="Assignment DB ID (from assign step)"
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Quoted Fee</label>
              <input type="number" value={form.quoted_fee} onChange={e=>setForm(f=>({...f,quoted_fee:e.target.value}))} placeholder="750"
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none" />
            </div>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Currency</label>
              <select value={form.currency} onChange={e=>setForm(f=>({...f,currency:e.target.value}))}
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
                {["USD","EUR","GBP","NGN","ZAR","KES"].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Fee Notes</label>
            <input value={form.fee_notes} onChange={e=>setForm(f=>({...f,fee_notes:e.target.value}))} placeholder="Negotiated rate for SME pilot project"
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none" />
          </div>
          <div><label className="text-xs font-medium text-slate-600 mb-1 block">Commercial Status</label>
            <select value={form.commercial_status} onChange={e=>setForm(f=>({...f,commercial_status:e.target.value}))}
              className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
              {COMMERCIAL_STATUSES.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
            </select>
          </div>
          {isEdit && (
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">Payment Status</label>
              <select value={form.payment_status} onChange={e=>setForm(f=>({...f,payment_status:e.target.value}))}
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none">
                {PAYMENT_STATUSES.map(s=><option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 h-10 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Engagement"}
          </button>
          <button onClick={onClose} className="px-4 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}
